/**
 * FOLLOWER BOOST — Hero 3D controller
 *
 * Responsibilities (all decorative; the page is complete without this file):
 *  - entrance choreography (adds .is-ready)
 *  - pointer tilt + multi-layer parallax (desktop, fine pointer only)
 *  - scroll transition (--scroll 0..1, lerped)
 *  - lime particle field on a <canvas>, paused off-screen / hidden tab
 *  - animated counter for the mock stat
 *
 * Performance rules: one rAF loop that sleeps when idle, DPR capped at 1.5,
 * writes only when a value changed, everything disabled for reduced motion.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');
const MOBILE = window.matchMedia('(max-width: 749px)');

class FbHero {
  /** @param {HTMLElement} el */
  constructor(el) {
    this.el = el;
    this.scene = el.querySelector('[data-fb-scene]');
    this.canvas = el.querySelector('[data-fb-particles]');

    this.wantTilt = el.dataset.tilt === 'true';
    this.wantParallax = el.dataset.parallax === 'true';
    this.wantParticles = el.dataset.particles === 'true';
    this.wantScroll = el.dataset.scrollFx === 'true';
    this.lite = el.classList.contains('fb-hero--mobile-lite') && MOBILE.matches;

    this.target = { x: 0, y: 0, scroll: 0 };
    this.current = { x: 0, y: 0, scroll: 0 };
    this.gyro = { x: 0, y: 0, active: false };
    this.hovering = false;
    this.visible = true;
    this.raf = 0;
    this.lastActive = performance.now();

    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.tick = this.tick.bind(this);

    this.init();
  }

  init() {
    // Entrance: next frame so the initial (hidden) state is painted first.
    requestAnimationFrame(() => requestAnimationFrame(() => this.el.classList.add('is-ready')));

    if (REDUCED.matches) return; // composition stays, numbers are already in the markup

    // Visibility gate for everything that costs frames.
    const io = new IntersectionObserver(
      (entries) => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.wake();
      },
      { threshold: 0.05 }
    );
    io.observe(this.el);

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.visible) this.wake();
    });

    if ((this.wantTilt || this.wantParallax) && FINE_POINTER.matches) {
      this.el.addEventListener('pointermove', this.onPointerMove, { passive: true });
      this.el.addEventListener('pointerleave', this.onPointerLeave, { passive: true });
    } else if (this.wantTilt || this.wantParallax) {
      this.initGyro();
    }

    if (this.wantScroll) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    if (this.wantParticles && this.canvas) {
      this.initParticles();
      window.addEventListener('resize', this.onResize, { passive: true });
    } else if (this.canvas) {
      this.canvas.remove();
      this.canvas = null;
    }

    this.animateCounter();

    this.wake();
  }

  /* ---------- pointer ---------- */
  onPointerMove(e) {
    this.hovering = true;
    const r = this.el.getBoundingClientRect();
    const nx = ((e.clientX - r.left) / r.width) * 2 - 1;
    const ny = ((e.clientY - r.top) / r.height) * 2 - 1;
    this.target.x = Math.max(-1, Math.min(1, nx));
    this.target.y = Math.max(-1, Math.min(1, ny));
    this.wake();
  }

  onPointerLeave() {
    this.hovering = false;
    this.target.x = 0;
    this.target.y = 0;
    this.wake();
  }

  /* ---------- gyroscope (touch devices) ---------- */
  initGyro() {
    if (!('DeviceOrientationEvent' in window)) return;
    const handler = (e) => {
      if (e.gamma == null || e.beta == null) return;
      // gamma: left/right tilt (-90..90), beta: front/back (-180..180); phones are usually held ~45deg
      const nx = Math.max(-1, Math.min(1, e.gamma / 28));
      const ny = Math.max(-1, Math.min(1, (e.beta - 45) / 32));
      this.gyro.x = nx;
      this.gyro.y = ny;
      this.gyro.active = true;
      this.wake();
    };
    const start = () => window.addEventListener('deviceorientation', handler, { passive: true });
    const DOE = window.DeviceOrientationEvent;
    if (typeof DOE.requestPermission === 'function') {
      // iOS: needs a user gesture; ask once on the first tap inside the hero, silently ignore refusals.
      const ask = () => {
        DOE.requestPermission().then((state) => state === 'granted' && start()).catch(() => {});
        this.el.removeEventListener('touchend', ask);
      };
      this.el.addEventListener('touchend', ask, { passive: true });
    } else {
      start();
    }
  }

  /* ---------- scroll ---------- */
  onScroll() {
    const r = this.el.getBoundingClientRect();
    const h = r.height || 1;
    // 0 while the hero top is at/above the viewport top, 1 when it has scrolled one hero-height away.
    const p = Math.min(1, Math.max(0, -r.top / (h * 0.9)));
    if (Math.abs(p - this.target.scroll) > 0.001) {
      this.target.scroll = p;
      this.wake();
    }
  }

  /* ---------- loop ---------- */
  wake() {
    this.lastActive = performance.now();
    if (!this.raf) this.raf = requestAnimationFrame(this.tick);
  }

  tick(now) {
    this.raf = 0;
    if (!this.visible || document.hidden) return;

    const c = this.current;
    const t = this.target;
    const k = 0.08;
    let moving = false;

    // Idle sway: when nothing drives the pointer, the scene breathes on its own
    // (slow Lissajous, tiny amplitude) so the 3D never reads as a still image.
    if (!this.hovering) {
      const sx = Math.sin(now / 4300) * 0.32;
      const sy = Math.cos(now / 6100) * 0.22;
      const gx = this.gyro.active ? this.gyro.x * 0.7 : 0;
      const gy = this.gyro.active ? this.gyro.y * 0.7 : 0;
      t.x = sx + gx;
      t.y = sy + gy;
      this.lastActive = now;
    }

    for (const key of ['x', 'y', 'scroll']) {
      const d = t[key] - c[key];
      if (Math.abs(d) > 0.0008) {
        c[key] += d * (key === 'scroll' ? 0.18 : k);
        moving = true;
      } else if (c[key] !== t[key]) {
        c[key] = t[key];
        moving = true;
      }
    }

    if (moving) {
      const s = this.el.style;
      if (this.wantParallax) {
        s.setProperty('--px', c.x.toFixed(4));
        s.setProperty('--py', c.y.toFixed(4));
      }
      if (this.wantTilt) {
        s.setProperty('--tilt-y', (c.x * 7).toFixed(3) + 'deg');
        s.setProperty('--tilt-x', (-c.y * 5).toFixed(3) + 'deg');
      }
      if (this.wantScroll) s.setProperty('--scroll', c.scroll.toFixed(4));
      this.lastActive = now;
    }

    const particlesAlive = this.particles ? this.stepParticles(now) : false;

    // Keep running while something moves or particles animate; sleep otherwise.
    if (moving || particlesAlive || now - this.lastActive < 120) {
      this.raf = requestAnimationFrame(this.tick);
    }
  }

  /* ---------- particles ---------- */
  initParticles() {
    this.ctx = this.canvas.getContext('2d', { alpha: true });
    this.dpr = Math.min(1.5, window.devicePixelRatio || 1);
    this.resizeCanvas();
    const count = this.lite ? 14 : MOBILE.matches ? 22 : 48;
    this.fps = this.lite ? 24 : 30;
    if (this.lite) this.dpr = 1;
    this.particles = Array.from({ length: count }, () => this.spawn(true));
    this.lastFrame = 0;
  }

  resizeCanvas() {
    const r = this.el.getBoundingClientRect();
    this.w = Math.max(1, Math.floor(r.width));
    this.h = Math.max(1, Math.floor(r.height));
    this.canvas.width = Math.floor(this.w * this.dpr);
    this.canvas.height = Math.floor(this.h * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  onResize() {
    if (!this.particles) return;
    this.resizeCanvas();
    this.wake();
  }

  spawn(anywhere) {
    // Bias particles to the right 60% of the hero where the stage lives.
    const x = this.w * (0.35 + Math.random() * 0.65);
    const y = anywhere ? Math.random() * this.h : this.h + 10;
    return {
      x,
      y,
      r: 0.6 + Math.random() * 1.6,
      vy: 0.12 + Math.random() * 0.35,
      vx: (Math.random() - 0.5) * 0.15,
      a: 0.15 + Math.random() * 0.5,
      tw: Math.random() * Math.PI * 2,
    };
  }

  stepParticles(now) {
    if (now - this.lastFrame < 1000 / this.fps) return true; // 24-30 fps is plenty for dust
    this.lastFrame = now;
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.w, this.h);
    ctx.fillStyle = '#b7ff3c';
    for (const p of this.particles) {
      p.y -= p.vy;
      p.x += p.vx + Math.sin(now / 1400 + p.tw) * 0.08;
      p.tw += 0.01;
      if (p.y < -10) Object.assign(p, this.spawn(false));
      ctx.globalAlpha = p.a * (0.6 + 0.4 * Math.sin(now / 900 + p.tw));
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    return true;
  }

  /* ---------- counters ---------- */
  /**
   * Animates every [data-fb-count] once the hero enters the viewport.
   * Accepts "58 243", "+10K", "+27,8 %", "1.2M": prefix, number, suffix are kept,
   * only the number rolls. Short, eased, no casino effect.
   */
  animateCounter() {
    const nodes = this.el.querySelectorAll('[data-fb-count]');
    if (!nodes.length) return;
    const run = () => nodes.forEach((el, i) => FbHero.rollNumber(el, 500 + i * 120));
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          run();
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(this.el);
  }

  static rollNumber(el, delay) {
    const raw = (el.dataset.fbCount || el.textContent).trim();
    const m = raw.match(/^([^\d]*)([\d\s.,\u202f\u00a0]+)(.*)$/);
    if (!m) return;
    let [, prefix, numStr, suffix] = m;
    const trailing = numStr.match(/\s+$/);
    if (trailing) {
      suffix = trailing[0] + suffix;
      numStr = numStr.trimEnd();
    }
    const clean = numStr.replace(/[\s\u202f\u00a0]/g, '');
    const decimal = clean.includes(',') ? ',' : clean.includes('.') && clean.split('.').pop().length <= 2 ? '.' : '';
    const value = parseFloat(decimal ? clean.replace(/[.,]/g, (c) => (c === decimal ? '.' : '')) : clean.replace(/[.,]/g, ''));
    if (!Number.isFinite(value)) return;
    const decimals = decimal ? (clean.split(decimal).pop() || '').length : 0;
    const groupSep = numStr.includes(' ') || numStr.includes('\u202f') || numStr.includes('\u00a0') ? ' ' : '';
    const fmt = (n) => {
      let [int, frac] = n.toFixed(decimals).split('.');
      if (groupSep) int = int.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep);
      return prefix + int + (frac ? decimal + frac : '') + suffix;
    };
    const start = performance.now() + delay;
    const dur = 1400;
    el.textContent = fmt(0);
    const step = (t) => {
      const p = Math.min(1, Math.max(0, (t - start) / dur));
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = p < 1 ? fmt(value * eased) : raw;
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
}

const boot = () => {
  document.querySelectorAll('[data-fb-hero]').forEach((el) => {
    if (!el.__fbHero) el.__fbHero = new FbHero(el);
  });
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

// Theme editor: re-init when the section is re-rendered.
document.addEventListener('shopify:section:load', boot);
